/**
 * API Configuration and Base Instance
 * Uses dual storage for Safari resilience
 */

import tokenStorage from '../utils/tokenStorage';

// In production, use relative URL (same origin). In development, use localhost.
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

// Prevent multiple simultaneous redirects to login
let isRedirectingToLogin = false;

// Prevent multiple simultaneous suspension redirects
let isHandlingSuspension = false;

// Helper function to get auth token (dual storage with auto-restore)
const getAuthToken = () => {
  return tokenStorage.getToken();
};

// Helper function to get active organization ID for multi-org support
const getActiveOrgId = () => {
  const user = tokenStorage.getUser();
  if (!user) return null;
  const org = user.primaryOrganization || user.organization;
  if (!org) return null;
  return org._id || org;
};

// Helper function to get participant info for QR code joiners
// Uses dual storage — checks session-specific, then general
const getParticipantInfo = (endpoint) => {
  try {
    // Try to extract sessionId from endpoint (e.g., /participant/sessions/:id/...)
    const sessionMatch = endpoint?.match(/\/sessions\/([a-f0-9]{24})/i);
    const sessionIdFromUrl = sessionMatch ? sessionMatch[1] : null;

    return tokenStorage.getParticipantData(sessionIdFromUrl);
  } catch {
    return null;
  }
};

/**
 * Global 401 handler
 * Detects auth failures and redirects appropriately.
 * - For authenticated users (token-based): redirect to /login
 * - For participants (header-based): redirect to /join
 * - Skips public endpoints that don't require auth
 */
const handle401 = (endpoint) => {
  if (isRedirectingToLogin) return;

  // Skip 401 handling for public auth endpoints (login, register, etc.)
  const publicEndpoints = ['/auth/login', '/auth/register', '/auth/check-email', '/auth/forgot-password', '/auth/reset-password'];
  if (publicEndpoints.some(pe => endpoint.startsWith(pe))) return;

  // Skip for public join endpoints
  if (endpoint.includes('/join-public') || endpoint.includes('/sessions/code/')) return;

  // Skip for participant session routes — these use optionalProtect on the backend
  // and should work even without a JWT token (via participant headers/cookies).
  // A 401 here is transient (e.g. stale token) and should NOT kick the participant out.
  if (endpoint.includes('/participant/sessions/')) return;

  // Skip for participant recovery and check-returning endpoints
  if (endpoint.includes('/participant/recover-session') || endpoint.includes('/participant/check-returning')) return;

  console.warn('[API] 401 Unauthorized on:', endpoint);

  // Determine if this is a participant route that truly requires auth
  const isParticipantRoute = endpoint.includes('/participant/');

  if (isParticipantRoute) {
    // Only redirect for participant routes that require full auth (dashboard, certificates, etc.)
    // Don't redirect if we have participant data in storage — the session is still recoverable
    const hasParticipantData = tokenStorage.getItem('current_participant') || tokenStorage.getItem('participantData');
    if (hasParticipantData) {
      console.warn('[API] Participant 401 but session data exists, NOT redirecting');
      return;
    }
    isRedirectingToLogin = true;
    console.warn('[API] Participant auth lost with no session data, redirecting to /join');
    window.location.href = '/join';
  } else {
    // Authenticated user lost token — clear auth tokens and redirect to login
    // IMPORTANT: Only clear auth tokens, NOT participant data.
    // A user might be in a live session as a participant — clearing participant data
    // would kick them out of the session they're actively participating in.

    // Guard: if the user is currently inside an active live session (trainer control,
    // participant session, active-session page), do NOT clear tokens or redirect.
    // A transient 401 on a peripheral API call (analytics, workbook metadata) must
    // not kick the user out of a session they are actively running / attending.
    const currentPath = window.location.pathname;
    const isInsideLiveSession = (
      currentPath.startsWith('/session/') ||
      /\/sessions\/[^/]+\/(control|active|live)/i.test(currentPath) ||
      /\/trainer\/sessions\/[^/]+/i.test(currentPath) ||
      /\/participant\/sessions\/[^/]+/i.test(currentPath)
    );
    if (isInsideLiveSession) {
      console.warn('[API] 401 during active live session — NOT clearing token or redirecting');
      return;
    }

    isRedirectingToLogin = true;
    tokenStorage.removeToken();
    tokenStorage.removeUser();
    console.warn('[API] Auth token invalid/expired, redirecting to /login');

    window.location.href = '/login';
  }

  // Reset flag after redirect
  setTimeout(() => { isRedirectingToLogin = false; }, 3000);
};

// Helper function to handle API errors
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error
    const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
    throw new Error(message);
  } else if (error.request) {
    // Request was made but no response
    throw new Error('No response from server. Please check your connection.');
  } else if (error instanceof Error) {
    // Already a thrown Error (e.g. from the !response.ok path below). Re-throw it
    // as-is so any custom props attached to it — like `userMessage` (a friendly,
    // user-facing string) or `code` — survive instead of being flattened away.
    throw error;
  } else {
    // Error setting up request
    throw new Error(error.message || 'An error occurred');
  }
};

// Headers every API request carries: staff JWT, active org, participant identity.
// Shared by apiFetch and api.download so a file download authenticates exactly like
// the JSON call next to it.
const buildRequestHeaders = (endpoint) => {
  const token = getAuthToken();
  const participantInfo = getParticipantInfo(endpoint);

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Include active organization ID for multi-org support
  const activeOrgId = getActiveOrgId();
  if (activeOrgId) {
    defaultHeaders['x-organization-id'] = activeOrgId;
  }

  // Always include participant info if available (even for authenticated users)
  // This handles the case where an authenticated user joins via public join (session code)
  if (participantInfo) {
    // Note: participantId comes from backend response, id is fallback
    const participantId = participantInfo.participantId || participantInfo.id || '';
    defaultHeaders['X-Participant-Id'] = participantId;
    defaultHeaders['X-Participant-Name'] = encodeURIComponent(participantInfo.name || '');
    defaultHeaders['X-Session-Id'] = participantInfo.sessionId || '';

    // Debug logging for participant headers
    if (endpoint.includes('/participant/')) {
      console.log('[API] Participant headers:', { participantId, name: participantInfo.name, sessionId: participantInfo.sessionId });
    }
  } else if (endpoint.includes('/participant/') && endpoint.includes('/sessions/')) {
    // Debug: warn if no participant info found for participant routes
    console.warn('[API] No participant info found for:', endpoint);
  }

  // Signed participant identity. Unlike the X-Participant-* headers above, the server
  // verifies this one, so routes that act on one person's own data trust only it.
  // Sent in its own header because Authorization may already carry a staff JWT.
  const urlSessionId = endpoint?.match(/\/sessions\/([a-f0-9]{24})/i)?.[1];
  const participantToken = tokenStorage.getParticipantToken(urlSessionId || participantInfo?.sessionId);
  if (participantToken) {
    defaultHeaders['X-Participant-Token'] = participantToken;
  }

  return defaultHeaders;
};

// Base fetch function with auth
const apiFetch = async (endpoint, options = {}) => {
  const defaultHeaders = buildRequestHeaders(endpoint);

  const config = {
    ...options,
    credentials: 'include', // Send cookies for Safari/iOS session recovery
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    // Global 401 handler — detect auth failure before parsing
    if (response.status === 401) {
      // Still parse the response for error message
      let data = {};
      try { data = await response.json(); } catch { /* ignore */ }
      handle401(endpoint);
      throw new Error(data.error || data.message || 'Not authorized');
    }

    // Read the body exactly once. A Response body is a one-shot stream: the 403 branch
    // below used to read it and then fall through to a second read, which threw
    // "body stream already read" and replaced every other 403 (feature_not_enabled,
    // participant_token_required, …) with an unreadable TypeError.
    let data = null;
    try { data = await response.json(); } catch { /* non-JSON body — handled below */ }

    // Organization suspension handler — 403 with specific error code
    if (response.status === 403 && data) {
      if (data.error === 'organization_suspended') {
        // Store suspension info so DashboardLayout can display the overlay
        try {
          localStorage.setItem('orgSuspended', 'true');
          localStorage.setItem('suspensionReason', data.suspensionReason || 'No reason provided');
          localStorage.setItem('suspendedAt', data.suspendedAt || '');
        } catch { /* ignore storage errors */ }

        if (!isHandlingSuspension) {
          isHandlingSuspension = true;
          // Dispatch event so DashboardLayout can react immediately
          window.dispatchEvent(new CustomEvent('orgSuspended', {
            detail: { reason: data.suspensionReason, suspendedAt: data.suspendedAt },
          }));
          setTimeout(() => { isHandlingSuspension = false; }, 3000);
        }

        throw new Error('Your organization has been suspended');
      }

      // Plan-expired handler — 403 with error: 'plan_expired'.
      // Fire a window event so DashboardLayout can toast the message,
      // then throw a typed error so callers can choose to handle it.
      if (data.error === 'plan_expired') {
        window.dispatchEvent(new CustomEvent('planExpired', {
          detail: { message: data.message, planEnd: data.planEnd },
        }));
        const err = new Error(data.message || 'Your plan has expired');
        err.code = 'plan_expired';
        err.planEnd = data.planEnd;
        throw err;
      }
    }

    if (data === null) {
      // Not JSON — e.g. an HTML error page from the proxy. Previously this surfaced as
      // a JSON SyntaxError; give callers a normal error with the status instead.
      const err = new Error(response.ok ? 'Unexpected response from server' : 'Request failed');
      err.status = response.status;
      throw err;
    }

    if (!response.ok) {
      const err = new Error(data.error || data.message || 'Request failed');
      // Carry through the machine code and friendly message so callers can show
      // a clean message (e.g. AI "high demand") instead of the raw error string.
      err.code = data.error;
      // Coded errors (feature_not_enabled, participant_token_required, …) put their
      // human-readable text in `message`; use it when no explicit userMessage is sent.
      if (data.userMessage || data.message) err.userMessage = data.userMessage || data.message;
      // Status and body, so callers can react to a specific failure rather than only
      // display it — e.g. a 409 from a compare-and-set carries the true server state
      // in `data`, which the caller should adopt instead of showing an error.
      err.status = response.status;
      err.data = data;
      throw err;
    }

    return data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// API Methods
export const api = {
  get: (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return apiFetch(url, { method: 'GET' });
  },

  post: (endpoint, data) => {
    return apiFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  put: (endpoint, data) => {
    return apiFetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: (endpoint) => {
    return apiFetch(endpoint, { method: 'DELETE' });
  },

  // For file uploads
  upload: async (endpoint, formData) => {
    const token = getAuthToken();
    const headers = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Include active organization ID for multi-org support
    const uploadOrgId = getActiveOrgId();
    if (uploadOrgId) {
      headers['x-organization-id'] = uploadOrgId;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });

    // Global 401 handler for uploads too
    if (response.status === 401) {
      handle401(endpoint);
      throw new Error('Not authorized');
    }

    /**
     * 413 comes from nginx, not the API — the request is rejected before Node sees it,
     * so the body is an HTML error page and the generic non-JSON branch below would
     * report "server error, contact support" for what is really "your file is too big".
     * That sent a real upload failure to support instead of to the person who could fix
     * it by picking a smaller file.
     */
    if (response.status === 413) {
      throw new Error('That file is too large to upload. Please use a smaller file.');
    }

    // Check content type before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Server returned non-JSON (likely HTML error page)
      const text = await response.text();
      console.error('Upload failed - server returned non-JSON:', text.substring(0, 200));
      throw new Error('Upload failed - server error. Please try again or contact support.');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Upload failed');
    }

    return data;
  },

  /**
   * GET a file (PDF, CSV) and save it. Errors come back as JSON from the API, so they
   * are thrown with the same shape apiFetch uses (message, code, status, userMessage).
   * The file name comes from the server's Content-Disposition, else `fallbackName`.
   */
  download: async (endpoint, fallbackName = 'download') => {
    const headers = buildRequestHeaders(endpoint);
    delete headers['Content-Type'];
    const response = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'GET', credentials: 'include', headers });

    if (response.status === 401) {
      handle401(endpoint);
      throw new Error('Not authorized');
    }
    if (!response.ok) {
      let data = {};
      try { data = await response.json(); } catch { /* not JSON */ }
      const err = new Error(data.error || data.message || 'Download failed');
      err.code = data.error;
      if (data.userMessage || data.message) err.userMessage = data.userMessage || data.message;
      err.status = response.status;
      throw err;
    }

    const disposition = response.headers.get('content-disposition') || '';
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || fallbackName;
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      link.remove();
      window.URL.revokeObjectURL(url);
    }, 200);
    return filename;
  },
};

export default api;
